// Recipe Module Phase 3 (docs/RECIPE_MODULE.md) — "✨ Generate with AI" entry point shared by
// PercentageRecipeEditorPage.js and DoughRecipeEditorPage.js. Describes the recipe in plain text,
// optionally grounded in the company's uploaded Knowledge Base books, and shows the draft for
// review before the parent editor prefills its own state from it (ingredients are returned as
// plain names, not linked to a real Ingredient — the user links/adjusts after applying).
import React, { useState } from 'react';
import knowledgeBaseService from '../services/knowledgeBaseService';
import AiProgressIndicator from '../pages/maintenance/AiProgressIndicator';

const STAGES = ['Reading your request...', 'Drafting the recipe...', 'Almost done...'];

export default function GenerateRecipeModal({ recipeType, onApply, onClose }) {
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError('');
    setDraft(null);
    try {
      const recipe = await knowledgeBaseService.generateSampleRecipe(recipeType, prompt.trim(), setProgressMessage);
      setDraft(recipe);
    } catch (err) {
      setError(err.message || 'Failed to generate a recipe.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">✨ Generate with AI</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}

            <label className="form-label small fw-semibold">Describe the recipe you want</label>
            <div className="input-group mb-2">
              <input className="form-control" value={prompt} onChange={e => setPrompt(e.target.value)}
                placeholder={recipeType === 'dough' ? 'e.g. A classic 65% hydration pizza dough with a biga' : 'e.g. A simple marinara sauce'}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleGenerate(); } }} disabled={generating} />
              <button className="btn btn-primary" onClick={handleGenerate} disabled={generating || !prompt.trim()}>
                {generating ? 'Generating…' : 'Generate'}
              </button>
            </div>
            <div className="text-muted mb-2" style={{ fontSize: 12 }}>
              Grounded in your uploaded Knowledge Base books when relevant — falls back to general
              culinary knowledge otherwise. Ingredients come back as plain names; you'll link them
              to your real ingredient catalog after applying the draft.
            </div>
            <AiProgressIndicator active={generating} message={progressMessage} messages={STAGES} />

            {draft && (
              <div className="card mt-3" style={{ border: '1px solid #e9ecef' }}>
                <div className="card-body">
                  <h6 className="fw-bold">{draft.name}</h6>
                  {draft.category && <div className="text-muted small mb-2">{draft.category}</div>}

                  {Array.isArray(draft.lines) && (
                    <>
                      <div className="text-uppercase text-muted small fw-bold mt-2 mb-1">Ingredients (% of final batch)</div>
                      <table className="table table-sm mb-2">
                        <tbody>
                          {draft.lines.map((l, idx) => (
                            <tr key={idx}><td>{l.ingredientName}</td><td className="text-end">{l.percentage}%</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  {Array.isArray(draft.flourLines) && (
                    <>
                      <div className="text-uppercase text-muted small fw-bold mt-2 mb-1">Flour Blend</div>
                      <table className="table table-sm mb-2">
                        <tbody>
                          {draft.flourLines.map((l, idx) => (
                            <tr key={idx}><td>{l.flourName}</td><td className="text-end">{l.percentOfFlour}%</td></tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="text-uppercase text-muted small fw-bold mt-2 mb-1">Formula (% of flour)</div>
                      <table className="table table-sm mb-2">
                        <tbody>
                          <tr><td>Hydration</td><td className="text-end">{draft.hydrationPercent}%</td></tr>
                          <tr><td>Salt</td><td className="text-end">{draft.saltPercent}%</td></tr>
                          <tr><td>Yeast ({draft.yeastType})</td><td className="text-end">{draft.yeastPercent}%</td></tr>
                          {(draft.extraIngredients || []).map((e, idx) => (
                            <tr key={idx}><td>{e.label} ({e.phase === 'preferment' ? 'pre-ferment' : 'final dough'})</td><td className="text-end">{e.percentOfFlour}%</td></tr>
                          ))}
                        </tbody>
                      </table>
                      {draft.preFermentType !== 'none' && (
                        <div className="text-muted small mb-2">
                          Pre-ferment: {draft.preFermentType} — {draft.preFermentFlourPercent}% of flour @ {draft.preFermentHydrationPercent}% hydration
                        </div>
                      )}
                    </>
                  )}

                  {Array.isArray(draft.steps) && draft.steps.length > 0 && (
                    <>
                      <div className="text-uppercase text-muted small fw-bold mt-2 mb-1">Steps</div>
                      <ol className="small mb-0">
                        {draft.steps.map((s, idx) => <li key={idx}>{s}</li>)}
                      </ol>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>Close</button>
            {draft && (
              <button className="btn btn-success" onClick={() => onApply(draft)}>✓ Use This Draft</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
